/* eslint-disable no-param-reassign */
import { Box, Button, Container, CssBaseline, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { createYupSchema } from 'src/modules/member/models/createYupSchema';
import { FormItemResponse, FormMemberResponse } from 'src/modules/member/models/formMemberResponse';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import { createWebFormMember, getNewWebFormMember } from 'src/shared/services/webFormMemberService';
import { FormMemberPostItem } from 'src/modules/member/models/formMemberPost';
import { WebFormMemberPost } from 'src/modules/member/models/webFormMemberPost';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { BigLoader } from 'src/shared/components/organisms/bigLoader';
import { SignupServerErrorSummary } from '../components/signupServerErrorSummary';

const SignUpPage = () => {
  const params = useParams();
  const isMounted = useIsMounted();
  const { t } = useTranslation(['member', 'common']);
  const [showCustomer, setShowCustomer] = useState<string>();
  const [formName, setFormName] = useState<string>('webform');
  const [loading, setIsLoading] = useState(true);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [fieldsReady, setFieldsReady] = useState<boolean>(false);
  const [payingMember, setPayingMember] = useState<boolean | undefined>(undefined);
  const [rowsAndColsOrder, setRowsAndColsOrder] = useState<any[]>([]);
  const [skipValidation, setSkipValidation] = useState<boolean>(false);

  const [memberFormSettings, setMemberFormSettings] = useState<FormMemberResponse>();
  const formMethods = useForm<FormMemberResponse>({
    defaultValues: {
      items: [{}],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
    trigger,
  } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  useEffect(() => {
    if (params.id) {
      setShowCustomer(params.id.trim() === 'apptech' ? 'Apptech' : params.id);
      // Todo: Hämta customer från WebFormMember/new
    }
  }, [params.id]);

  useEffect(() => {
    const fetchFormSettings = async () => {
      const result = await getNewWebFormMember(params.id);
      if (isMounted()) {
        const uniqueRowIds = new Set<number>();
        // Sort by row and column, start by finding all unique rows
        result.data.items
          .filter((object) => object.formKey !== null)
          .forEach((object) => {
            uniqueRowIds.add(object.formSetting.row);
          });
        // add default values for null
        result.data.items.map((item) => {
          const nullCheck = (val: any) => {
            if (val === null) {
              if (typeof item.formValue === 'number') {
                return 0;
              }
              return '';
            }
            return val;
          };

          return Object.assign(item, { value: nullCheck(item.formValue) });
        });

        interface ColumnOrder {
          cols: string[];
        }

        const mappedFormItems: ColumnOrder[] = [];
        let rowIndex = 0;
        let previousRow = 0;
        const sortedFormItems: FormItemResponse[] = [];
        Array.from(uniqueRowIds)
          .sort((a, b) => a - b)
          .forEach((x) => {
            const colItems = result.data.items
              .filter((item) => item.formKey && item.formSetting.row === x)
              .sort((c, d) => c.formSetting.col - d.formSetting.col);

            colItems.forEach((child) => {
              // Lock some fields for both paing and not paying member on create
              if (child.formKey === 'resDate' || child.formKey === 'resCause') {
                child.formSetting.locked = true;
                child.formValue = '';
              }
              // Lock some fields if not paying member
              if (!payingMember && (child.formKey === 'admDate' || child.formKey === 'expireDate')) {
                child.formSetting.locked = true;
                child.formValue = '';
              }
              // Set default value to today when creating a new unpaid member
              if (payingMember && child.formKey === 'admDate') {
                child.formValue = dayjs(new Date()).format('YYYYMMDD');
              }
              if (payingMember && child.formKey === 'expireDate') {
                child.formSetting.locked = true;
                child.formValue = '';
              }
              sortedFormItems.push(child);
            });

            if (x !== previousRow) {
              rowIndex += 1;
            }

            mappedFormItems[rowIndex] = { cols: colItems.map((y) => y.formKey) };
            previousRow = x;
          });

        const yepSchema = sortedFormItems.reduce(createYupSchema, {});

        setRowsAndColsOrder(mappedFormItems);
        reset({ items: sortedFormItems });
        setFieldsReady(true);
        setMemberFormSettings({ items: sortedFormItems } as FormMemberResponse);
        setIsLoading(false);
      }
    };
    // if (payingMember !== undefined) {
    fetchFormSettings();

    // }
  }, [formName, isMounted, params.id, payingMember, reset]);

  const saveFormData = React.useCallback(
    async (data: FormMemberResponse) => {
      const formMemberPutItems: FormMemberPostItem[] = [];

      data.items.forEach((item) => {
        formMemberPutItems.push({
          formKey: item.formKey,
          formValue: Array.isArray(item.value) ? item.value.filter((x) => x !== null).join(',') : item.value,
        });
      });

      const formData: WebFormMemberPost = {
        skipValidation,
        items: formMemberPutItems,
        custName: params?.id || '',
        formName,
        url: '',
      };
      ConsoleHelper.log('formData to post', formData);
      try {
        const res = await createWebFormMember(formData);
        if (isMounted()) {
          toast.success(<>{t('member:editMember.saveSuccessMessage')}</>);
          setServerErrors([]);
          if (res.data) {
            window.location.replace(res.data.url);
          }
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setServerErrors(e.data);
        }
      }
    },
    [skipValidation, params?.id, formName, isMounted, t],
  );

  const onSubmit = React.useCallback(
    async (data: FormMemberResponse) => {
      // if (data.admDate) data.admDate = dayjs(data.admDate).format('YYYMMDDD');
      await trigger();
      ConsoleHelper.log('Data: ', data);
      await saveFormData(data);
    },
    [saveFormData, trigger],
  );

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <Container maxWidth='sm'>
      <CssBaseline />
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2, border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'), position: 'relative' }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', p: 6, flexDirection: 'column' }}>
              <BigLoader message={t('common:statusMessages.loadingForm')} />
            </Box>
          )}
          {isSubmitting && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                p: 6,
                flexDirection: 'column',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 3000,
                backgroundColor: 'rgba(255,255,255,.7)',
              }}
            >
              <BigLoader message={t('common:statusMessages.savingForm')} />
            </Box>
          )}
          {serverErrors.length > 0 && <SignupServerErrorSummary serverErrors={serverErrors} />}
          <GridWrapper>
            <Grid item xs={12}>
              <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* <GridWrapper>
                        {fields && fieldsReady && fields.map((item, index) => <DynamicFormField key={item.id} formItem={item} index={index} />)}
                      </GridWrapper> */}
                  {fieldsReady &&
                    fields &&
                    rowsAndColsOrder &&
                    rowsAndColsOrder.map((item) => {
                      return (
                        <Box sx={{ my: 1, mx: 1, mb: 3 }} key={item.cols[0]}>
                          <GridWrapper>
                            {item.cols.map((x) => {
                              const current = fields.filter((y: any) => y.formKey === x)[0];
                              const currentIndex = fields.findIndex((y) => y.id === current.id);
                              return <DynamicFormField key={current.id} formItem={current} index={currentIndex} renderFullWidth />;
                            })}
                          </GridWrapper>
                        </Box>
                      );
                    })}
                  <Stack spacing={2} direction='row'>
                    <Button size='large' variant='contained' fullWidth color='success' onClick={onHandleSave} disableElevation disabled={loading}>
                      {t('common:signup.toPaymentButton')}
                    </Button>
                  </Stack>
                </form>
              </FormProvider>
            </Grid>
          </GridWrapper>
        </Box>
      </Paper>
      <Box sx={{ mb: 3, mt: 1, textAlign: 'right' }}>
        <Typography variant='caption'>
          Powered by <Link href='https://foreningssupport.se'>Föreningssupport</Link>
        </Typography>
      </Box>
    </Container>
  );
};

export default SignUpPage;
